import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Select from '@mui/material/Select';
import { IoClose as Close } from 'react-icons/io5';
import { MdPerson as PersonIcon, MdDynamicForm as FieldIcon } from 'react-icons/md';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import './AddRuleModal.scss';

const cn = 'addRuleModal';

export default function AddRuleModal({
    open,
    onClose,
    onSave,
    title,
    rule,
    onRuleChange,
    onFieldChange,
    onUserChange,
    validationError,
    ruleTypes,
    conditions,
    comparisonOperators,
    property,
    fieldParameter,
    formFieldList,
    filteredUsers,
    userSearchTerm,
    setUserSearchTerm,
    saveDisabled,
}) {
    if (!rule) return null;

    const getEmailPlaceholder = (condition) => {
        if (condition === 'DOMAIN') return 'example.com';
        if (condition === 'CONTAINS') return 'gmail  (any substring)';
        return 'user@example.com';
    };

    const getEmailHint = (condition) => {
        if (condition === 'EQUALS' || condition === 'NOT EQUALS') return 'Must be a full email address';
        if (condition === 'CONTAINS') return 'Any substring — e.g. "gmail" matches all Gmail addresses';
        if (condition === 'DOMAIN') return 'Domain only — no @ symbol (e.g. example.com)';
        return null;
    };

    const renderReporterSection = () => (
        <>
            <div className={`${cn}__formGroup`}>
                <label className={`${cn}__label`}>Property <span className={`${cn}__required`}>*</span></label>
                <Select
                    fullWidth
                    size='small'
                    value={rule.property}
                    onChange={(e) => onRuleChange('property', e.target.value)}
                    className={`${cn}__select`}>
                    <MenuItem value="" disabled>Select a property</MenuItem>
                    {property.map((prop, idx) => (
                        <MenuItem key={idx} value={prop}>
                            {prop === 'fullName' ? 'Full Name' :
                             prop === 'phoneNumber' ? 'Phone Number' :
                             prop === 'email' ? 'Email' :
                             prop === 'domain' ? 'Domain' : prop?.toUpperCase()}
                        </MenuItem>
                    ))}
                </Select>
            </div>

            {rule.property && (
                <>
                    <div className={`${cn}__formGroup`}>
                        <label className={`${cn}__label`}>Condition <span className={`${cn}__required`}>*</span></label>
                        <div className={`${cn}__chipGroup`}>
                            {conditions.map((cond, idx) => (
                                <button
                                    key={idx}
                                    className={`${cn}__chip${rule.condition === cond ? ` ${cn}__chip--active` : ''}`}
                                    onClick={() => onRuleChange('condition', cond)}>
                                    {cond}
                                </button>
                            ))}
                        </div>
                    </div>

                    {rule.condition && (
                        <div className={`${cn}__formGroup`}>
                            <label className={`${cn}__label`}>Value <span className={`${cn}__required`}>*</span></label>
                            <TextField
                                fullWidth
                                size='small'
                                value={rule.paramValue}
                                onChange={(e) => onRuleChange('paramValue', e.target.value)}
                                placeholder={
                                    rule.property === 'email' ? getEmailPlaceholder(rule.condition) :
                                    rule.property === 'phoneNumber' ? '+1 234 567 8900' :
                                    'Enter value'
                                }
                                error={!!validationError}
                                className={`${cn}__textField`}
                            />
                            {rule.property === 'email' && getEmailHint(rule.condition) && !validationError && (
                                <span className={`${cn}__hint`}>{getEmailHint(rule.condition)}</span>
                            )}
                            {validationError && (
                                <span className={`${cn}__validationError`}>{validationError}</span>
                            )}
                        </div>
                    )}

                    {rule.paramValue && !validationError && (
                        <div className={`${cn}__formGroup`}>
                            <label className={`${cn}__label`}>Assign User <span className={`${cn}__required`}>*</span></label>
                            {renderUserSelect()}
                        </div>
                    )}
                </>
            )}
        </>
    );

    const renderFieldSection = () => (
        <>
            <div className={`${cn}__formGroup`}>
                <label className={`${cn}__label`}>Field <span className={`${cn}__required`}>*</span></label>
                <Select
                    fullWidth
                    size='small'
                    value={rule.fieldId}
                    onChange={(e) => onFieldChange(e.target.value)}
                    className={`${cn}__select`}>
                    <MenuItem value={0} disabled>Select a field</MenuItem>
                    {fieldParameter.map((field, idx) => (
                        <MenuItem key={idx} value={field.id}>
                            {`${idx + 1}. ${field.title?.toUpperCase()}`}
                        </MenuItem>
                    ))}
                </Select>
            </div>

            {rule.fieldId && (
                <>
                    {(rule.property === 'date' || rule.property === 'information' || rule.property === 'file' || rule.property === 'condition') && (
                        <div className={`${cn}__errorBanner`}>
                            Cannot set rules for <strong>{rule.property}</strong> fields. Please select a different field.
                        </div>
                    )}

                    {(rule.property === 'text' || rule.property === 'phoneNumber') && (
                        <>
                            {renderConditionChips(conditions)}
                            {rule.condition && renderValueTextField(
                                rule.property === 'phoneNumber' ? '+1 234 567 8900' : 'Enter value'
                            )}
                        </>
                    )}

                    {(rule.property === 'rating' || rule.property === 'number') && (
                        <>
                            {renderConditionChips(comparisonOperators)}
                            {rule.condition && (
                                <div className={`${cn}__formGroup`}>
                                    <label className={`${cn}__label`}>Value <span className={`${cn}__required`}>*</span></label>
                                    <TextField
                                        fullWidth
                                        type='number'
                                        size='small'
                                        value={rule.paramValue}
                                        onChange={(e) => onRuleChange('paramValue', e.target.value)}
                                        placeholder='Type a number…'
                                        className={`${cn}__textField`}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {rule.property === 'multiChoice' && (
                        <>
                            {renderConditionChips(conditions)}
                            {rule.condition && (
                                <div className={`${cn}__formGroup`}>
                                    <label className={`${cn}__label`}>Value <span className={`${cn}__required`}>*</span></label>
                                    <Select
                                        fullWidth
                                        size='small'
                                        value={rule.paramValue || 0}
                                        onChange={(e) => onRuleChange('paramValue', e.target.value)}
                                        className={`${cn}__select`}>
                                        <MenuItem value={0} disabled>Select option</MenuItem>
                                        {formFieldList.find(item => item.id === rule.fieldId)?.options?.map((opt, idx) => (
                                            <MenuItem key={idx} value={opt?.id}>
                                                {opt?.title?.toUpperCase()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </>
                    )}

                    {rule.property === 'multiSelect' && (
                        <>
                            {renderConditionChips(conditions)}
                            {rule.condition && (
                                <div className={`${cn}__formGroup`}>
                                    <label className={`${cn}__label`}>Values <span className={`${cn}__required`}>*</span></label>
                                    <Select
                                        fullWidth
                                        size='small'
                                        multiple
                                        value={rule.paramValue || []}
                                        onChange={(e) => onRuleChange('paramValue', e.target.value)}
                                        className={`${cn}__select`}>
                                        {formFieldList.find(item => item.id === rule.fieldId)?.options?.map((opt, idx) => (
                                            <MenuItem key={idx} value={opt?.id}>
                                                {opt?.title?.toUpperCase()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </>
                    )}

                    {rule.paramValue && !(rule.property === 'date' || rule.property === 'information' || rule.property === 'file' || rule.property === 'condition') && (
                        <div className={`${cn}__formGroup`}>
                            <label className={`${cn}__label`}>Assign User <span className={`${cn}__required`}>*</span></label>
                            {renderUserSelect()}
                        </div>
                    )}
                </>
            )}
        </>
    );

    const renderConditionChips = (condList) => (
        <div className={`${cn}__formGroup`}>
            <label className={`${cn}__label`}>Condition <span className={`${cn}__required`}>*</span></label>
            <div className={`${cn}__chipGroup`}>
                {condList.map((cond, idx) => (
                    <button
                        key={idx}
                        className={`${cn}__chip${rule.condition === cond ? ` ${cn}__chip--active` : ''}`}
                        onClick={() => onRuleChange('condition', cond)}>
                        {cond}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderValueTextField = (placeholder) => (
        <div className={`${cn}__formGroup`}>
            <label className={`${cn}__label`}>Value <span className={`${cn}__required`}>*</span></label>
            <TextField
                fullWidth
                size='small'
                value={rule.paramValue}
                onChange={(e) => onRuleChange('paramValue', e.target.value)}
                placeholder={placeholder}
                error={!!validationError}
                className={`${cn}__textField`}
            />
            {validationError && (
                <span className={`${cn}__validationError`}>{validationError}</span>
            )}
        </div>
    );

    const renderUserSelect = () => (
        <Select
            fullWidth
            size='small'
            value={rule.assignee || ''}
            onOpen={() => setUserSearchTerm('')}
            MenuProps={{ autoFocus: false, MenuListProps: { autoFocusItem: false } }}
            onChange={(e) => onUserChange(e.target.value)}
            className={`${cn}__select`}>
            <ListSubheader className={`${cn}__searchHeader`}>
                <TextField
                    size='small'
                    fullWidth
                    placeholder='Search users…'
                    value={userSearchTerm}
                    onChange={(e) => { e.stopPropagation(); setUserSearchTerm(e.target.value); }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    inputProps={{ style: { padding: '8px 4px' } }}
                />
            </ListSubheader>
            {filteredUsers.length === 0 ? (
                <MenuItem disabled>No users found</MenuItem>
            ) : (
                filteredUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                        {user.fullName}
                    </MenuItem>
                ))
            )}
        </Select>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            className={`${cn}__dialog`}>

            <DialogTitle className={`${cn}__title`}>
                <span className={`${cn}__title__text`}>{title}</span>
                <button className={`${cn}__title__close`} onClick={onClose} aria-label='Close'>
                    <Close />
                </button>
            </DialogTitle>

            <DialogContent className={`${cn}__content`}>
                <div className={`${cn}__form`}>
                    {/* Rule Type Toggle */}
                    <div className={`${cn}__formGroup`}>
                        <label className={`${cn}__label`}>Rule Type <span className={`${cn}__required`}>*</span></label>
                        <div className={`${cn}__typeToggle`}>
                            {ruleTypes.map((type, idx) => (
                                <button
                                    key={idx}
                                    className={`${cn}__typeBtn${rule.type === type ? ` ${cn}__typeBtn--active` : ''}`}
                                    onClick={() => onRuleChange('type', type)}>
                                    <span className={`${cn}__typeBtn__icon`}>
                                        {type === 'reporter' ? <PersonIcon /> : <FieldIcon />}
                                    </span>
                                    {type === 'reporter' ? 'Reporter' : 'Field'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`${cn}__divider`} />

                    {rule.type === 'reporter' && renderReporterSection()}
                    {rule.type === 'field' && renderFieldSection()}
                </div>
            </DialogContent>

            <DialogActions className={`${cn}__actions`}>
                <button className={`${cn}__cancelBtn`} onClick={onClose}>
                    Cancel
                </button>
                <BorderBlueButtonV2
                    text={title === 'Edit Rule' ? 'Update Rule' : 'Add Rule'}
                    onClick={() => onSave(rule)}
                    disabled={saveDisabled}
                />
            </DialogActions>
        </Dialog>
    );
}
